// react
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

// context
import { ContextSSE, SSEConnectionStatus, SSENotification } from './SSE.context';

export interface SSEProviderProps {
	autoConnect?: boolean;
	children: ReactNode;
	maxNotifications?: number;
	onConnect?: (notification: SSENotification) => void;
	onDisconnect?: () => void;
	onError?: (error: Event) => void;
	onNotification?: (notification: SSENotification) => void;
	token?: string;
	url?: string;
	userId?: string;
}

export const SSEProvider = ({
	autoConnect = false,
	children,
	maxNotifications = 50,
	onConnect,
	onDisconnect,
	onError,
	onNotification,
	token,
	url,
	userId,
}: SSEProviderProps) => {
	// state
	const [connectionId, setConnectionId] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [messageCount, setMessageCount] = useState(0);
	const [notifications, setNotifications] = useState<SSENotification[]>([]);
	const [status, setStatus] = useState<SSEConnectionStatus>('disconnected');
	const [uptime, setUptime] = useState(0);

	// refs
	const refAbortController = useRef<AbortController | null>(null);
	const refConnectionStartTime = useRef<number | null>(null);
	const refEventSource = useRef<EventSource | null>(null);
	const refOnConnect = useRef(onConnect);
	const refOnDisconnect = useRef(onDisconnect);
	const refOnError = useRef(onError);
	const refOnNotification = useRef(onNotification);
	const refReconnectAttempts = useRef(0);
	const refReconnectTimeout = useRef<NodeJS.Timeout | null>(null);
	const refToken = useRef(token);
	const refUptimeInterval = useRef<NodeJS.Timeout | null>(null);
	const refUrl = useRef(url);
	const refUserId = useRef(userId);

	// update refs when props change
	useEffect(() => {
		refOnConnect.current = onConnect;
		refOnDisconnect.current = onDisconnect;
		refOnError.current = onError;
		refOnNotification.current = onNotification;
		refToken.current = token;
		refUrl.current = url;
		refUserId.current = userId;
	}, [onConnect, onDisconnect, onError, onNotification, token, url, userId]);

	// generate UUID
	const generateUUID = useCallback(() => {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}, []);

	// reset connection state
	const resetConnectionState = useCallback(() => {
		setIsConnected(false);
		setStatus('disconnected');
		setConnectionId(null);
		setUptime(0);
		refConnectionStartTime.current = null;

		if (refUptimeInterval.current) {
			clearInterval(refUptimeInterval.current);
			refUptimeInterval.current = null;
		}
		if (refReconnectTimeout.current) {
			clearTimeout(refReconnectTimeout.current);
			refReconnectTimeout.current = null;
		}
	}, []);

	// add notification
	const addNotification = useCallback(
		(notification: SSENotification) => {
			const enrichedNotification: SSENotification = {
				...notification,
				id: notification.id || generateUUID(),
				receivedAt: new Date().toISOString(),
			};

			setNotifications((prev) => {
				const updated = [enrichedNotification, ...prev];
				return updated.slice(0, maxNotifications);
			});

			if (refOnNotification.current) {
				refOnNotification.current(enrichedNotification);
			}
		},
		[generateUUID, maxNotifications]
	);

	// disconnect
	const disconnect = useCallback(() => {
		if (refEventSource.current) {
			// Clear stability timer if pending
			if (
				(
					refEventSource.current as EventSource & {
						_stabilityTimer?: ReturnType<typeof setTimeout>;
					}
				)._stabilityTimer
			) {
				clearTimeout(
					(
						refEventSource.current as EventSource & {
							_stabilityTimer?: ReturnType<typeof setTimeout>;
						}
					)._stabilityTimer
				);
			}
			refEventSource.current.close();
			refEventSource.current = null;
		}

		if (refAbortController.current) {
			refAbortController.current.abort();
			refAbortController.current = null;
		}

		resetConnectionState();

		if (refOnDisconnect.current) {
			refOnDisconnect.current();
		}
	}, [resetConnectionState]);

	// connect
	const connect = useCallback(async () => {
		const sseUrl = refUrl.current;
		const sseUserId = refUserId.current;
		const sseToken = refToken.current;

		if (!sseUrl || !sseUserId) {
			console.error('[SSEProvider] Missing url or userId');
			return;
		}

		// clear any pending reconnect
		if (refReconnectTimeout.current) {
			clearTimeout(refReconnectTimeout.current);
			refReconnectTimeout.current = null;
		}

		setStatus('connecting');

		if (sseToken) {
			// === FETCH STREAMING PATH (POST /api/connect with JWT) ===

			if (refAbortController.current) {
				refAbortController.current.abort();
				refAbortController.current = null;
			}

			const abortController = new AbortController();
			refAbortController.current = abortController;

			try {
				// sseUrl is the indexer API base (e.g. .../bldt/indexer/api).
				const connectUrl = `${sseUrl}/connect`;
				const response = await fetch(connectUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${sseToken}`,
					},
					body: JSON.stringify({ userId: sseUserId }),
					signal: abortController.signal,
				});

				if (!response.ok) {
					console.error(`[SSEProvider] Connect failed: ${response.status}`);
					setIsConnected(false);
					setStatus('error');
					// Don't auto-reconnect on 401 (token expired/invalid)
					if (response.status !== 401 && refUrl.current && refUserId.current) {
						const attempt = refReconnectAttempts.current;
						const delay = Math.min(3000 * Math.pow(2, attempt), 60000);
						refReconnectAttempts.current = attempt + 1;
						console.warn(`[SSEProvider] Reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);
						refReconnectTimeout.current = setTimeout(() => {
							if (refUrl.current && refUserId.current) {
								connect();
							}
						}, delay);
					}
					return;
				}

				if (!response.body) {
					console.error('[SSEProvider] No response body');
					setStatus('error');
					return;
				}

				// Connection established — start stability timer and uptime
				const stabilityTimer = setTimeout(() => {
					refReconnectAttempts.current = 0;
				}, 30000);

				setIsConnected(true);
				setStatus('connected');
				refConnectionStartTime.current = Date.now();

				if (refUptimeInterval.current) {
					clearInterval(refUptimeInterval.current);
				}
				refUptimeInterval.current = setInterval(() => {
					if (refConnectionStartTime.current) {
						const seconds = Math.floor((Date.now() - refConnectionStartTime.current) / 1000);
						setUptime(seconds);
					}
				}, 1000);

				// Stream SSE messages
				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';
				let done = false;

				while (!done) {
					const result = await reader.read();
					done = result.done;
					if (done) break;
					const value = result.value;

					buffer += decoder.decode(value, { stream: true });
					const parts = buffer.split('\n\n');
					buffer = parts.pop() || '';

					for (const part of parts) {
						const dataLine = part.split('\n').find((l) => l.startsWith('data: '));
						if (!dataLine) continue;

						const jsonStr = dataLine.slice(6);
						try {
							const notification: SSENotification = JSON.parse(jsonStr);
							setMessageCount((prev) => prev + 1);

							if (notification.type === 'connected') {
								setConnectionId(notification.transactionId || null);
								if (refOnConnect.current) {
									refOnConnect.current(notification);
								}
							}

							if (notification.type !== 'ping') {
								addNotification(notification);
							}
						} catch (e) {
							console.error('[SSEProvider] Failed to parse notification:', jsonStr);
						}
					}
				}

				clearTimeout(stabilityTimer);
				// Stream ended cleanly (server closed)
				setIsConnected(false);
				setStatus('disconnected');
				if (refOnDisconnect.current) {
					refOnDisconnect.current();
				}
			} catch (error: unknown) {
				if (error instanceof Error && error.name === 'AbortError') {
					// Intentional disconnect — do not reconnect
					return;
				}
				console.error('[SSEProvider] Fetch SSE error:', error);
				setIsConnected(false);
				setStatus('error');
				if (refOnError.current) {
					refOnError.current(error as Event);
				}

				const attempt = refReconnectAttempts.current;
				const delay = Math.min(3000 * Math.pow(2, attempt), 60000);
				refReconnectAttempts.current = attempt + 1;
				console.warn(`[SSEProvider] Reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);
				refReconnectTimeout.current = setTimeout(() => {
					if (refUrl.current && refUserId.current) {
						connect();
					}
				}, delay);
			}
		} else {
			// === EVENTSOURCE PATH (GET /api/notifications, no auth header) ===

			if (refEventSource.current) {
				refEventSource.current.close();
				refEventSource.current = null;
			}

			try {
				const eventSource = new EventSource(`${sseUrl}/notifications?userId=${sseUserId}`);
				refEventSource.current = eventSource;

				eventSource.onopen = () => {
					const stabilityTimer = setTimeout(() => {
						refReconnectAttempts.current = 0;
					}, 30000);
					(
						eventSource as EventSource & { _stabilityTimer?: ReturnType<typeof setTimeout> }
					)._stabilityTimer = stabilityTimer;

					setIsConnected(true);
					setStatus('connected');
					refConnectionStartTime.current = Date.now();

					if (refUptimeInterval.current) {
						clearInterval(refUptimeInterval.current);
					}
					refUptimeInterval.current = setInterval(() => {
						if (refConnectionStartTime.current) {
							const seconds = Math.floor((Date.now() - refConnectionStartTime.current) / 1000);
							setUptime(seconds);
						}
					}, 1000);
				};

				eventSource.onmessage = (event) => {
					try {
						const notification: SSENotification = JSON.parse(event.data);

						setMessageCount((prev) => prev + 1);

						if (notification.type === 'connected') {
							setConnectionId(notification.transactionId || null);
							if (refOnConnect.current) {
								refOnConnect.current(notification);
							}
						}

						if (notification.type !== 'ping') {
							addNotification(notification);
						}
					} catch (e) {
						console.error('[SSEProvider] Failed to parse notification:', event.data);
						addNotification({
							payload: { message: `Parse error: ${event.data}` },
							type: 'error',
						});
					}
				};

				eventSource.onerror = (event) => {
					if (
						(eventSource as EventSource & { _stabilityTimer?: ReturnType<typeof setTimeout> })
							._stabilityTimer
					) {
						clearTimeout(
							(eventSource as EventSource & { _stabilityTimer?: ReturnType<typeof setTimeout> })
								._stabilityTimer
						);
					}

					eventSource.close();
					refEventSource.current = null;

					setIsConnected(false);
					setStatus('error');

					if (refOnError.current) {
						refOnError.current(event);
					}

					const attempt = refReconnectAttempts.current;
					const delay = Math.min(3000 * Math.pow(2, attempt), 60000);
					refReconnectAttempts.current = attempt + 1;

					console.warn(
						`[SSEProvider] Connection error. Reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`
					);

					refReconnectTimeout.current = setTimeout(() => {
						if (refUrl.current && refUserId.current) {
							connect();
						}
					}, delay);
				};
			} catch (error) {
				console.error('[SSEProvider] Failed to create EventSource:', error);
				resetConnectionState();
			}
		}
	}, [addNotification, resetConnectionState]);

	// clear notifications
	const clearNotifications = useCallback(() => {
		setNotifications([]);
		setMessageCount(0);
	}, []);

	// send test notification
	const sendTestNotification = useCallback(
		async (message?: string): Promise<boolean> => {
			const sseUrl = refUrl.current;
			const sseUserId = refUserId.current;

			if (!isConnected || !sseUrl || !sseUserId) {
				console.error(
					'[SSEProvider] Cannot send notification: not connected or missing configuration'
				);
				return false;
			}

			const payload = {
				message: message || `Test message at ${new Date().toLocaleTimeString()}`,
				transactionId: generateUUID(),
				type: 'manual_test',
				userId: sseUserId,
			};

			try {
				const response = await fetch(`${sseUrl}/notifications`, {
					body: JSON.stringify(payload),
					headers: {
						'Content-Type': 'application/json',
					},
					method: 'POST',
				});

				return response.ok;
			} catch (error) {
				console.error('[SSEProvider] Failed to send test notification:', error);
				return false;
			}
		},
		[generateUUID, isConnected]
	);

	// auto connect when url and userId are available
	useEffect(() => {
		if (autoConnect && url && userId) {
			connect();
		}

		return () => {
			disconnect();
		};
		// only run on mount/unmount or when autoConnect/url/userId change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoConnect, url, userId]);

	// cleanup on unmount
	useEffect(() => {
		return () => {
			if (refEventSource.current) {
				refEventSource.current.close();
			}
			if (refAbortController.current) {
				refAbortController.current.abort();
			}
			if (refUptimeInterval.current) {
				clearInterval(refUptimeInterval.current);
			}
			if (refReconnectTimeout.current) {
				clearTimeout(refReconnectTimeout.current);
			}
		};
	}, []);

	return (
		<ContextSSE.Provider
			value={{
				clearNotifications,
				connect,
				connectionId,
				disconnect,
				isConnected,
				messageCount,
				notifications,
				sendTestNotification,
				status,
				uptime,
			}}>
			{children}
		</ContextSSE.Provider>
	);
};
